import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { UserCustomListTargetTypeType } from '../../__generated__/graphql.ts'
import type { NoveltyMeta } from '@/stores/user-lists'

type PendingListAdd = {
  targetType: UserCustomListTargetTypeType
  itemTitle: string
  releaseEntryId?: string
  relationId?: string
  meta?: NoveltyMeta
}

type OverviewSession = {
  activeTabKey: string
  tabOffsetStart: number
  tabOffsetEnd: number
  listScrollByTab: Record<string, number>
  listSkipByTab: Record<string, number>
}

type ListDetailSession = {
  scrollByListId: Record<string, number>
  skipByListId: Record<string, number>
}

type ReleaseDetailSession = {
  scrollByReleaseId: Record<string, number>
  skipByReleaseId: Record<string, number>
  activeTabByReleaseId: Record<string, string>
}

type VolumeDetailSession = {
  scrollByKey: Record<string, number>
}

type CollectionDetailSession = {
  scrollByKey: Record<string, number>
}

type ExplorationSession = {
  activeTabKey: string
  tabScrollPositions: Record<string, number>
  previewListScrollTop: number
  previewListSkip: number
}

type ListsSession = {
  tabScrollPositions: Record<string, number>
}

const isBackNavFlag = ref(false)
if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
  window.addEventListener(
    'popstate',
    () => {
      isBackNavFlag.value = true
    },
    { capture: true },
  )
}

export const useSessionStateStore = defineStore('sessionState', () => {
  const overview = reactive<OverviewSession>({
    activeTabKey: '',
    tabOffsetStart: -1,
    tabOffsetEnd: 2,
    listScrollByTab: {},
    listSkipByTab: {},
  })

  const listDetail = reactive<ListDetailSession>({
    scrollByListId: {},
    skipByListId: {},
  })

  const releaseDetail = reactive<ReleaseDetailSession>({
    scrollByReleaseId: {},
    skipByReleaseId: {},
    activeTabByReleaseId: {},
  })

  const volumeDetail = reactive<VolumeDetailSession>({
    scrollByKey: {},
  })

  const collectionDetail = reactive<CollectionDetailSession>({
    scrollByKey: {},
  })

  const exploration = reactive<ExplorationSession>({
    activeTabKey: '',
    tabScrollPositions: {},
    previewListScrollTop: 0,
    previewListSkip: 0,
  })

  const lists = reactive<ListsSession>({
    tabScrollPositions: {},
  })

  const pendingListAdd = ref<PendingListAdd | null>(null)

  function setPendingListAdd(pending: PendingListAdd) {
    pendingListAdd.value = pending
  }

  function consumePendingListAdd(): PendingListAdd | null {
    const pending = pendingListAdd.value
    pendingListAdd.value = null
    return pending
  }

  function markBackNav() {
    isBackNavFlag.value = true
  }

  function consumeBackNav(): boolean {
    const was = isBackNavFlag.value
    isBackNavFlag.value = false
    return was
  }

  return { markBackNav, consumeBackNav, setPendingListAdd, consumePendingListAdd, overview, listDetail, releaseDetail, volumeDetail, collectionDetail, exploration, lists }
})
